// Copyright (c) Ping. All rights reserved.
//
// Thin platform-agnostic facade over `react-native-toast-message`.
// Web and macOS hosts mount the `<Toast />` provider once near their
// app root; callers anywhere in the tree fire toasts via `pingToast.*`
// without thinking about which platform they're on. The library
// itself works identically on web (react-native-web) and macOS.
//
// We deliberately don't expose the full library surface — keeping the
// helper narrow (success / error / info) avoids "type ambiguity"
// regressions where a caller picks a custom `type` the host didn't
// register a renderer for.

import Toast from 'react-native-toast-message';

export interface ToastOptions {
  readonly title: string;
  readonly description?: string;
  /**
   * Visible duration in ms. Defaults: 3500 (success/info), 5000 (error).
   * Pass `Infinity` for sticky toasts that the user must dismiss.
   */
  readonly visibilityMs?: number;
}

function show(type: 'success' | 'error' | 'info', opts: ToastOptions): void {
  const visibilityTime =
    opts.visibilityMs === undefined
      ? type === 'error'
        ? 5000
        : 3500
      : opts.visibilityMs === Infinity
        ? 99_999_999
        : opts.visibilityMs;
  Toast.show({
    type,
    text1: opts.title,
    ...(opts.description !== undefined ? { text2: opts.description } : {}),
    visibilityTime,
    autoHide: opts.visibilityMs !== Infinity,
    position: 'bottom',
  });
}

export const pingToast = {
  success(opts: ToastOptions): void {
    show('success', opts);
  },
  error(opts: ToastOptions): void {
    show('error', opts);
  },
  info(opts: ToastOptions): void {
    show('info', opts);
  },
  hide(): void {
    Toast.hide();
  },
};

// Re-export the underlying `<Toast />` provider for hosts to mount.
// Naming `<PingToastProvider />` so it's obvious which library it is
// at the host-mount site (and so consumers don't have to import the
// raw `Toast` themselves).
export { default as PingToastProvider } from 'react-native-toast-message';
